import type { ConnectorInstance, ConnectorSending, ConnectorTemplate, ConnectorsView } from '../../lib/api'
import type { Tone } from '../kit'

// The integrations catalogue, arranged by the job each integration does.
//
// # WHERE A CARD COMES FROM
//
// Three places, and each card says which it is. Most are templates the API
// serves from its connector catalogue, with this tenant's instance beside them.
// A few are views of a record kept on another tab (a risk register, the mail
// service, the Entra ID proof), and those send a person to that tab rather than
// offering a second form for the same record. The rest are plans: integrations
// that are designed and not built, which appear as "Coming next" with no
// Connect, because a card that offers a connection nobody can make is how a
// catalogue stops being believed.
//
// # WHY ORACLE FUSION CLOUD IS A PLAN AND A TEMPLATE AT ONCE
//
// The connector is being built beside this screen. Until the API serves a
// template with the key oracle_fusion, the plan card stands in for it, first
// and featured in ERP and procurement; the moment the template arrives the plan
// card is dropped and the real one takes its place with nothing to redeploy
// here. Every plan works the same way, keyed on the template it will become.
//
// # THE COPY RULES
//
// A card line is one sentence of at most 80 characters with no full stop, and
// names only what the integration connects to. Anything longer is in the
// drawer.

export type CategoryKey = 'erp' | 'identity' | 'messaging' | 'registers' | 'alerts' | 'tickets' | 'security' | 'documents'

export const CATEGORIES: { key: CategoryKey; name: string; line: string }[] = [
  { key: 'erp', name: 'ERP and procurement', line: 'Suppliers in, purchase clearance out' },
  { key: 'identity', name: 'Identity and access', line: "Proof that a leaving vendor's accounts are closed" },
  { key: 'messaging', name: 'Vendor messaging', line: 'How invitations and reminders reach a vendor' },
  { key: 'registers', name: 'Risk registers', line: 'Where vendor risk goes, and where other risk comes from' },
  { key: 'alerts', name: 'Alerts and chat', line: 'Where somebody is told, at the moment it matters' },
  { key: 'tickets', name: 'Tickets', line: 'Work raised for somebody outside this product' },
  { key: 'security', name: 'Security logs', line: "This product's own events, shipped to your security team" },
  // Not one of the seven jobs the design names, and still a connection a tenant
  // makes here. Dropping it would leave object storage configurable nowhere.
  { key: 'documents', name: 'Document storage', line: "Where a vendor's certificates and contracts are kept" },
]

// The API's categories, mapped to a job. IDENTITY, MAIL and PLATFORM are not
// here: sign-in, mail and the platform pairing each have a tab of their own,
// and every deployment has exactly one of each. Mail and the Entra ID proof
// still appear, as cards that open those tabs.
const CATEGORY_OF: Record<string, CategoryKey> = {
  PROCUREMENT: 'erp',
  // The GST and company register watch is shown on Purchase requests, and a
  // buyer is the person who asks whether a supplier's GSTIN is still live.
  INTELLIGENCE: 'erp',
  DIRECTORY: 'identity',
  MESSAGING: 'messaging',
  RISK_REGISTER: 'registers',
  ALERTING: 'alerts',
  TICKETING: 'tickets',
  SIEM: 'security',
  STORAGE: 'documents',
}

// Templates the API serves whose delivery is not built. Microsoft Sentinel and
// Syslog have a connection test that can reach a workspace or a collector and
// nothing that ever sends an event to either, so they say Coming next and offer
// no Connect. A connection somebody already made can still be opened and
// removed.
export const NOT_DELIVERING = new Set(['sentinel_logs', 'microsoft_sentinel', 'syslog'])

// Two letters on a colour dark enough to carry white text: every colour here
// measures at least 6 to 1 against white.
export const MARKS: Record<string, { mark: string; color: string }> = {
  oracle_fusion: { mark: 'OF', color: '#a33a2b' },
  oracle_ebs: { mark: 'OE', color: '#8a3324' },
  sap_s4hana: { mark: 'S4', color: '#1565a8' },
  sap_ariba: { mark: 'AR', color: '#1f5f9e' },
  procurement_webhook: { mark: 'BW', color: '#3d4a61' },
  statutory_relay: { mark: 'GS', color: '#6a4a12' },
  coupa: { mark: 'CO', color: '#1d5aa8' },
  dynamics_365_finance: { mark: 'D3', color: '#2e3b9e' },
  netsuite: { mark: 'NS', color: '#1f4e79' },
  workday: { mark: 'WD', color: '#0f5f7f' },
  entra_proof: { mark: 'EN', color: '#0a5aa8' },
  okta: { mark: 'OK', color: '#1f2a44' },
  sailpoint: { mark: 'SP', color: '#0b6b66' },
  mail: { mark: 'EM', color: '#0f5fa8' },
  whatsapp_cloud: { mark: 'WA', color: '#0f6e3c' },
  msg91: { mark: 'MS', color: '#2b50aa' },
  twilio: { mark: 'TW', color: '#b3243a' },
  gupshup: { mark: 'GU', color: '#4a3a8a' },
  foctta_core_risk: { mark: 'FC', color: '#172a52' },
  servicenow_irm: { mark: 'SN', color: '#2a6f4f' },
  generic_risk_out: { mark: 'RO', color: '#3d4a61' },
  generic_risk_in: { mark: 'RI', color: '#4b5563' },
  slack: { mark: 'SL', color: '#4a154b' },
  microsoft_teams: { mark: 'MT', color: '#464eb8' },
  pagerduty: { mark: 'PD', color: '#0b6b35' },
  generic_webhook: { mark: 'WH', color: '#3d4a61' },
  jira: { mark: 'JI', color: '#0c52cc' },
  servicenow: { mark: 'SN', color: '#2a6f4f' },
  splunk_hec: { mark: 'SK', color: '#2d3436' },
  elastic_siem: { mark: 'EL', color: '#005a80' },
  datadog_logs: { mark: 'DD', color: '#632ca6' },
  sentinel_logs: { mark: 'SE', color: '#0063a6' },
  microsoft_sentinel: { mark: 'SE', color: '#0063a6' },
  syslog: { mark: 'SY', color: '#4b5563' },
  s3_documents: { mark: 'S3', color: '#8a4b00' },
}

// One line per card, at most 80 characters, no full stop. Each is what the
// template's own description or the integration research says it does, and no
// more.
const LINES: Record<string, string> = {
  oracle_fusion: 'Reads suppliers, holds new purchase orders on every site until cleared',
  sap_s4hana: 'Supplier events in, purchasing block kept until the supplier is cleared',
  sap_ariba: 'Answers supplier registration approvals in Ariba with the clearance',
  procurement_webhook: 'Every clearance change as signed JSON to a buying system you run',
  statutory_relay: "Checks each supplier's GSTIN and company status every week",
  mail: 'Invitations and reminders sent from your own mail service',
  whatsapp_cloud: 'Reminders and replies from your own WhatsApp Business number',
  msg91: 'DLT-registered SMS for Indian mobile numbers',
  twilio: 'SMS through Twilio, a second route beside MSG91',
  gupshup: 'Checks a Gupshup key only; nothing is sent through it',
  foctta_core_risk: 'Vendor risks out to FOCTTA Core, decisions back',
  servicenow_irm: 'Vendor risks out to ServiceNow risk management, decisions back',
  generic_risk_out: 'Every vendor risk posted as JSON to a register you run',
  generic_risk_in: 'A register you already keep posts its risks here',
  slack: 'Posts to a channel when a vendor needs attention',
  microsoft_teams: 'The same alerts, posted to a Teams channel',
  pagerduty: 'Raises an incident for what should wake somebody',
  generic_webhook: 'The same alerts as JSON to a URL you own',
  jira: 'Raises an issue for a finding and closes it when resolved here',
  servicenow: 'Raises a record for a finding in the table your team works from',
  splunk_hec: 'Assessment, finding and approval events into Splunk',
  elastic_siem: 'The same events, indexed into Elasticsearch or OpenSearch',
  datadog_logs: 'The same events, sent to Datadog Logs',
  sentinel_logs: 'Events into your Sentinel workspace, once delivery is built',
  microsoft_sentinel: 'Events into your Sentinel workspace, once delivery is built',
  syslog: 'Syslog to your collector, once delivery is built',
  s3_documents: 'Certificates and contracts kept in S3, or anything with the same API',
}

// A step in "What it does": a short title and one line under it.
export type Step = { title: string; line: string }
// A row of the field mapping: what is read or written on the far side, and
// what it is here.
export type Mapping = { from: string; to: string; fromCode?: boolean; toCode?: boolean }

// What an integration does, step by step. Keys without an entry show the
// template's own description instead.
export const STEPS: Record<string, Step[]> = {
  oracle_fusion: [
    { title: 'Suppliers come in', line: 'New suppliers every 10 minutes, matched by PAN then GSTIN, never by name' },
    { title: 'Spend authorization asks us', line: 'When switched on, approved when cleared and left waiting while pending' },
    { title: 'Oracle enforces the answer', line: 'New purchasing documents held on each site until the supplier is cleared' },
    { title: 'Only our own holds are lifted', line: 'A hold someone else placed stays; new sites are re-checked every hour' },
  ],
  sap_s4hana: [
    { title: 'Suppliers come in', line: 'Business partner events from SAP Event Mesh become purchase requests' },
    { title: 'S/4HANA enforces the answer', line: 'The purchasing block stays on until the supplier is cleared here' },
    { title: 'The record carries the result', line: 'Tier, clearance and next review written to the fields you name' },
  ],
  sap_ariba: [
    { title: 'Suppliers are read', line: 'Supplier records read through the Supplier Data API' },
    { title: 'Ariba asks us as an approver', line: 'Registration and qualification approvals wait for this product' },
    { title: 'The clearance is the answer', line: 'Cleared is approved, blocked can be denied, pending waits' },
  ],
  entra_proof: [
    { title: 'Accounts are found', line: "Guest accounts carrying the vendor's mail domain, and its group" },
    { title: 'Read during the exit', line: 'Every 15 minutes while the vendor is being offboarded' },
    { title: 'Who closed each one', line: 'The directory audit log says who disabled an account and when' },
    { title: 'The checklist shows it', line: 'Accounts closed shows n of m, beside what a person recorded' },
  ],
  mail: [
    { title: 'Invitations leave', line: 'A questionnaire invitation goes from your own mail service' },
    { title: 'Reminders follow', line: 'On the schedule set in WhatsApp and SMS, email first' },
    { title: 'A test proves it', line: 'A real message to a mailbox you can open, from the Email tab' },
  ],
  whatsapp_cloud: [
    { title: 'Messages leave', line: 'Invitation, reminders and questions in wording Meta has approved' },
    { title: 'Receipts come back', line: 'Delivered and read receipts arrive at the receipt address' },
    { title: 'A test proves it', line: 'It can send only once a test message to your phone is delivered' },
  ],
  msg91: [
    { title: 'Messages leave', line: 'The same messages as SMS, in wording registered on DLT' },
    { title: 'Receipts come back', line: 'Delivery reports arrive at the receipt address' },
    { title: 'A test proves it', line: 'It can send only once a test message to your phone is delivered' },
  ],
}

export const MAPPINGS: Record<string, Mapping[]> = {
  oracle_fusion: [
    { from: 'SupplierNumber', fromCode: true, to: 'Supplier link' },
    { from: 'Supplier', fromCode: true, to: 'Legal name' },
    { from: 'TaxpayerId', fromCode: true, to: 'PAN' },
    { from: 'taxRegistrations', fromCode: true, to: 'GSTIN' },
    { from: 'Clearance', to: 'HoldAllNewPurchasingDocumentsFlag', toCode: true },
    { from: 'Blocked, when set', to: 'HoldAllInvoicesFlag', toCode: true },
  ],
  sap_s4hana: [
    { from: 'Supplier', fromCode: true, to: 'Supplier link' },
    { from: 'BusinessPartnerPanNumber', fromCode: true, to: 'PAN' },
    { from: 'Tax number of category IN3', to: 'GSTIN' },
    { from: 'Clearance', to: 'PurchasingIsBlocked', toCode: true },
    { from: 'Blocked, when set', to: 'PostingIsBlocked', toCode: true },
  ],
}

export const SETUP: Record<string, Step[]> = {
  oracle_fusion: [
    { title: 'Connect an integration user', line: 'Basic sign-in first, OAuth through your identity domain when ready' },
    { title: 'Choose procurement business units', line: 'Holds apply to purchasing sites in these units' },
    { title: 'Test on your Oracle test environment', line: 'Reads suppliers and sites, checks a hold can be written' },
  ],
}

// Said once on Oracle's drawer, planned or built: the hold is proven on the
// customer's own test environment before any customer switches it on.
export const PROOF_NOTE: Record<string, string> = {
  oracle_fusion: 'Hold behaviour is proven on your Oracle test environment before it is switched on',
}

// Fields shown in a drawer's form in groups, in an order a person fills them
// in. The API returns fields required first and then alphabetically, which is
// stable and nothing more. A key missing here is shown in the API's order.
export const FIELD_GROUPS: Record<string, { label: string; keys: string[] }[]> = {
  oracle_fusion: [
    {
      label: 'Sign in',
      keys: ['baseUrl', 'authMethod', 'username', 'password', 'tokenUrl', 'clientId', 'clientSecret', 'privateKey', 'scope'],
    },
    { label: 'Holds', keys: ['procurementBUs', 'paymentHoldWhenBlocked', 'inactivateOnExit'] },
    { label: 'Suppliers and approvals', keys: ['intake', 'panSource', 'answerSpendAuthorization', 'denyWhenBlocked', 'readSpend'] },
  ],
}

// Whether a field applies, given what else has been chosen. Oracle's sign-in
// fields depend on the way of signing in; showing all nine and a help line
// saying which three matter is how the wrong three get filled in.
export function fieldApplies(templateKey: string, fieldKey: string, values: Record<string, string>): boolean {
  if (templateKey !== 'oracle_fusion') return true
  const method = values.authMethod || 'basic'
  const signIn: Record<string, string[]> = {
    basic: ['username', 'password'],
    oauth_client_credentials: ['tokenUrl', 'clientId', 'clientSecret', 'scope'],
    // The integration user is the subject of the assertion, when it is set.
    oauth_jwt_assertion: ['tokenUrl', 'clientId', 'privateKey', 'scope', 'username'],
  }
  const allSignIn = ['username', 'password', 'tokenUrl', 'clientId', 'clientSecret', 'privateKey', 'scope']
  if (allSignIn.includes(fieldKey)) return (signIn[method] ?? allSignIn).includes(fieldKey)
  if (fieldKey === 'denyWhenBlocked') return values.answerSpendAuthorization === 'yes'
  return true
}

// ------------------------------------------------------------------- plans

type Plan = {
  key: string
  // The template key the built integration will have. When the API serves it,
  // this plan is dropped.
  becomes: string
  category: CategoryKey
  name: string
  line: string
  featured?: boolean
  steps: Step[]
  note: string
}

export const COMING_NEXT: Plan[] = [
  {
    key: 'plan:oracle_fusion',
    becomes: 'oracle_fusion',
    category: 'erp',
    name: 'Oracle Fusion Cloud',
    line: LINES.oracle_fusion!,
    featured: true,
    steps: STEPS.oracle_fusion!,
    note: 'Being built now, and can be connected here once it is ready',
  },
  {
    key: 'plan:oracle_ebs',
    becomes: 'oracle_ebs',
    category: 'erp',
    name: 'Oracle E-Business Suite',
    line: 'Suppliers read inside your network, supplier hold set where it is enforced',
    steps: [
      { title: 'Runs inside your network', line: 'An agent reads suppliers and sites from your EBS database' },
      { title: 'Supplier hold written', line: 'Through a narrow REST service, per operating unit' },
      { title: 'Only where EBS enforces it', line: 'The test reports refused where Enforce Supplier Hold is off' },
    ],
    note: 'Planned after Oracle Fusion Cloud. Not built yet',
  },
  {
    key: 'plan:coupa',
    becomes: 'coupa',
    category: 'erp',
    name: 'Coupa',
    line: 'Suppliers in, supplier status and risk back',
    steps: [
      { title: 'Suppliers come in', line: 'Suppliers changed since the last read' },
      { title: 'Status goes back', line: 'A supplier put on hold until it is cleared' },
    ],
    note: 'Planned. Not built yet',
  },
  {
    key: 'plan:dynamics_365_finance',
    becomes: 'dynamics_365_finance',
    category: 'erp',
    name: 'Dynamics 365 Finance',
    line: 'Vendors in, vendor put on hold for purchase orders until cleared',
    steps: [
      { title: 'Vendors come in', line: 'Vendors read through the data API with your directory sign-in' },
      { title: 'Dynamics enforces the answer', line: 'The vendor on hold for purchase orders until it is cleared' },
    ],
    note: 'Planned. Not built yet',
  },
  {
    key: 'plan:netsuite',
    becomes: 'netsuite',
    category: 'erp',
    name: 'NetSuite',
    line: 'Vendors read in, with a purchasing block through a script in your account',
    steps: [
      { title: 'Vendors come in', line: 'Vendors read through the REST API and SuiteQL' },
      { title: 'A block needs a script', line: 'NetSuite has no native block, so a script in your account holds it' },
    ],
    note: 'Planned. Not built yet',
  },
  {
    key: 'plan:workday',
    becomes: 'workday',
    category: 'erp',
    name: 'Workday',
    line: 'Suppliers read in, supplier status written back where your setup allows',
    steps: [
      { title: 'Suppliers come in', line: 'Suppliers read through the Resource Management service' },
      { title: 'Status goes back', line: 'What a hold means depends on how your Workday is set up' },
    ],
    note: 'Planned. Not built yet',
  },
  {
    key: 'plan:entra_proof',
    becomes: 'entra_proof',
    category: 'identity',
    name: 'Microsoft Entra ID',
    line: "Shows who closed each leaving vendor's account, and when",
    featured: true,
    steps: STEPS.entra_proof!,
    note: 'Not in this deployment yet',
  },
  {
    key: 'plan:okta',
    becomes: 'okta',
    category: 'identity',
    name: 'Okta',
    line: 'The same account closing proof for people who sign in through Okta',
    steps: [
      { title: 'Accounts are found', line: "The vendor's people in your Okta organisation" },
      { title: 'Status is read', line: 'Suspended or deprovisioned counts as closed' },
    ],
    note: 'Planned. Not built yet',
  },
  {
    key: 'plan:sailpoint',
    becomes: 'sailpoint',
    category: 'identity',
    name: 'SailPoint',
    line: "A vendor's identities and their access, read from SailPoint",
    steps: [{ title: 'Identities are read', line: "The vendor's non-employee identities and what they can reach" }],
    note: 'Planned. Not built yet',
  },
]

// The order cards take inside a category. A key not listed keeps the API's
// order, after the listed ones.
const ORDER = [
  'oracle_fusion',
  'oracle_ebs',
  'sap_s4hana',
  'sap_ariba',
  'coupa',
  'dynamics_365_finance',
  'netsuite',
  'workday',
  'procurement_webhook',
  'statutory_relay',
  'entra_proof',
  'okta',
  'sailpoint',
  'mail',
  'whatsapp_cloud',
  'msg91',
  'twilio',
  'gupshup',
]

// ------------------------------------------------------------------- cards

export type CardStatus = 'connected' | 'attention' | 'paused' | 'not_connected' | 'not_set_up' | 'check_only' | 'coming_next'

export type CardKind =
  // A template with its form in the drawer.
  | 'template'
  // A view of a record kept on another tab.
  | 'register'
  | 'mail'
  | 'entra_proof'
  // Designed and not built.
  | 'plan'

export type CatalogueCard = {
  id: string
  // The key the drawer's per-integration copy is looked up by.
  key: string
  kind: CardKind
  category: CategoryKey
  name: string
  mark: string
  markColor: string
  line: string
  featured: boolean
  status: CardStatus
  statusWord: string
  tone: Tone
  template?: ConnectorTemplate
  instance?: ConnectorInstance
  sending?: ConnectorSending
  plan?: Plan
  // Where a view card's record is managed.
  managedAt?: { href: string; label: string }
}

const WORD: Record<CardStatus, { word: string; tone: Tone }> = {
  connected: { word: 'Connected', tone: 'ok' },
  attention: { word: 'Needs attention', tone: 'warn' },
  paused: { word: 'Paused', tone: 'neutral' },
  not_connected: { word: 'Not connected', tone: 'neutral' },
  not_set_up: { word: 'Not set up', tone: 'neutral' },
  check_only: { word: 'Check only', tone: 'neutral' },
  coming_next: { word: 'Coming next', tone: 'brand' },
}

// The status an instance earns. Connected means proved and delivering: a
// connector that was reached and not proved (DRAFT), that failed its test, or
// whose last deliveries failed needs attention, and so does a messaging card
// that has not yet had a test message delivered, because "the key works" is not
// "a vendor gets the reminder".
function instanceStatus(instance: ConnectorInstance, sending?: ConnectorSending): CardStatus {
  if (instance.status === 'PAUSED') return 'paused'
  if (instance.status !== 'ACTIVE' || instance.consecutiveFailures > 0) return 'attention'
  if (sending && !sending.probeOnly && !sending.canSend) return 'attention'
  return 'connected'
}

function lineFor(key: string, what: string): string {
  const known = LINES[key]
  if (known) return known
  // A template this screen has no line for yet: its own first sentence, cut
  // to the limit at a word, without the full stop.
  const first = (what.split(/(?<=\.)\s/)[0] ?? what).replace(/\.$/, '')
  if (first.length <= 80) return first
  return first.slice(0, 78).replace(/\s+\S*$/, '') + '…'
}

function card(
  base: Omit<CatalogueCard, 'statusWord' | 'tone' | 'mark' | 'markColor'> & { mark?: string; markColor?: string },
): CatalogueCard {
  const m = MARKS[base.key]
  const initials = base.name
    .replace(/[^A-Za-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('')
  return {
    ...base,
    mark: base.mark ?? m?.mark ?? initials,
    markColor: base.markColor ?? m?.color ?? '#3d4a61',
    statusWord: WORD[base.status].word,
    tone: WORD[base.status].tone,
  }
}

export function buildCatalogue(view: ConnectorsView): CatalogueCard[] {
  const out: CatalogueCard[] = []
  const served = new Set<string>()

  for (const g of view.groups) {
    for (const { template: t, instance, sending } of g.items) {
      served.add(t.key)

      if (g.category === 'MAIL') continue
      if (g.category === 'IDENTITY') {
        // The Entra ID card signs people in, on Identity and SSO. Once it can
        // also prove a leaving vendor's accounts are closed (the template has
        // the offboardingProof switch), that second job is a card here.
        if (t.key === 'entra_id' && t.fields.some((f) => f.key === 'offboardingProof')) {
          served.add('entra_proof')
          const on = instance?.config?.offboardingProof === 'yes'
          out.push(
            card({
              id: 'entra_proof',
              key: 'entra_proof',
              kind: 'entra_proof',
              category: 'identity',
              name: 'Microsoft Entra ID',
              line: "Shows who closed each leaving vendor's account, and when",
              featured: true,
              status: instance && on ? instanceStatus(instance) : 'not_set_up',
              template: t,
              instance,
              managedAt: { href: '#/settings?t=sso', label: 'Open Identity and SSO' },
            }),
          )
        }
        continue
      }
      const category = t.key === 'oracle_fusion' ? 'erp' : CATEGORY_OF[g.category]
      if (!category) continue

      const register = t.category === 'RISK_REGISTER' && t.direction !== 'INBOUND'
      let status: CardStatus
      if (NOT_DELIVERING.has(t.key)) status = 'coming_next'
      else if (sending?.probeOnly) status = instance && instance.status === 'FAILING' ? 'attention' : 'check_only'
      else if (!instance) status = 'not_connected'
      else status = instanceStatus(instance, sending)

      out.push(
        card({
          id: t.key,
          key: t.key,
          kind: register ? 'register' : 'template',
          category,
          name: t.displayName,
          line: lineFor(t.key, t.what),
          featured: t.key === 'oracle_fusion',
          status,
          template: t,
          instance,
          sending,
          managedAt: register ? { href: '#/settings?t=registers', label: 'Open Risk registers' } : undefined,
        }),
      )
    }
  }

  // Mail is one card for the one mail service a deployment has, whichever of
  // the five ways it sends.
  const mail = view.groups.find((g) => g.category === 'MAIL')
  if (mail) {
    const held = mail.items.find((i) => i.instance)
    out.push(
      card({
        id: 'mail',
        key: 'mail',
        kind: 'mail',
        category: 'messaging',
        name: 'Email',
        line: held ? lineFor('', 'Sending through ' + held.template.displayName) : LINES.mail!,
        featured: false,
        status: held?.instance ? instanceStatus(held.instance) : 'not_set_up',
        template: held?.template,
        instance: held?.instance,
        managedAt: { href: '#/settings?t=email', label: 'Open Email' },
      }),
    )
  }

  for (const plan of COMING_NEXT) {
    if (served.has(plan.becomes)) continue
    out.push(
      card({
        id: plan.key,
        key: plan.becomes,
        kind: 'plan',
        category: plan.category,
        name: plan.name,
        line: plan.line,
        featured: Boolean(plan.featured),
        status: 'coming_next',
        plan,
      }),
    )
  }

  const rank = (c: CatalogueCard) => {
    const i = ORDER.indexOf(c.key)
    return i < 0 ? ORDER.length : i
  }
  // Array.prototype.sort is stable, so cards the order does not list keep the
  // API's order among themselves.
  return out.sort((a, b) => Number(b.featured) - Number(a.featured) || rank(a) - rank(b))
}

// The word on a card saying what opening it is for.
export function actionWord(c: CatalogueCard, mayWrite: boolean): string {
  if (c.kind === 'plan') return 'View plan'
  if (!mayWrite) return 'Details'
  switch (c.status) {
    case 'connected':
    case 'paused':
      return 'Manage'
    case 'attention':
      return 'Fix'
    case 'not_connected':
      return 'Connect'
    case 'not_set_up':
      return 'Set up'
    case 'check_only':
      return c.instance ? 'Manage' : 'Open'
    case 'coming_next':
      return c.instance ? 'Manage' : 'Details'
  }
}

export type Filter = 'all' | 'connected' | 'attention' | 'coming_next'

export function matches(c: CatalogueCard, f: Filter): boolean {
  return f === 'all' || c.status === f
}

// Source systems as a purchase request names them, with the mark the catalogue
// gives the same system, so a row on Purchase requests and a card here look
// alike.
export const SOURCE_SYSTEMS: Record<string, { name: string; mark: string; color: string }> = {
  ORACLE_FUSION: { name: 'Oracle Fusion', mark: 'OF', color: MARKS.oracle_fusion!.color },
  ORACLE_EBS: { name: 'Oracle EBS', mark: 'OE', color: MARKS.oracle_ebs!.color },
  SAP_S4HANA: { name: 'SAP S/4HANA', mark: 'S4', color: MARKS.sap_s4hana!.color },
  SAP_ARIBA: { name: 'SAP Ariba', mark: 'AR', color: MARKS.sap_ariba!.color },
  SAP_ECC: { name: 'SAP ECC', mark: 'EC', color: '#1f4e79' },
  COUPA: { name: 'Coupa', mark: 'CO', color: MARKS.coupa!.color },
}
